import React, { useEffect, useState } from 'react';
import { Button, Tag } from '@bedrock/components';
import { getCopyNode, getKey, getTabItemsFormProps } from '@mpc/common/src/features/MPCList/utils';
import MPCList, { MPCListProps, MPCListRef } from '@mpc/common/src/features/MPCList/index';
import DotDotDot from '@mpc/common/src/components/DotDotDot';
import {
  outsourceCommonFilterOptions,
  outsourceV2IpCooperationDemandAll,
  outsourceV2IpCooperationDemandPending,
  type IpCooperationCopyrightOwnerListForm,
} from 'services/api';
import { FILTER_OPTIONS } from './utils';
import DetailDrawer from '../DetailDrawer';

// 路由 key → 列表类型（待办/全部）
const routeToStateMap: Record<string, 'TODO' | 'ALL'> = {
  outIpTodo: 'TODO',
  outIpAll: 'ALL',
};

// 前端固定列（对齐 IpCooperationCopyrightOwnerListVo；接口不返回 columns，由前端注入）
// 列顺序对齐走查目标：需求单号 | 状态 | 所属产品 | IP名称 | 需求标题 | 预估金额 | 商务人员 | 操作
const COLUMNS = [
  { key: 'demand_id', name: '需求单号' },
  { key: 'status', name: '状态' },
  { key: 'product_name', name: '所属产品' },
  { key: 'ip_name', name: 'IP名' },
  { key: 'demand_title', name: '需求标题' },
  { key: 'estimated_amount', name: '预估金额' },
  { key: 'business_users', name: '商务人员' },
  { key: 'operation', name: '操作' },
];

// 列表「状态」列 Tag 配置（对齐外包端状态枚举文案；color 参考走查图：Offer谈判橙 / Offer待确认蓝 / Offer已确认绿）
const STATUS_TAG_CONF: Record<
  string,
  { color: 'light-orange' | 'light-blue' | 'light-green' }
> = {
  'Offer谈判': { color: 'light-orange' },
  'Offer待确认': { color: 'light-blue' },
  'Offer已确认': { color: 'light-green' },
};

const List: React.FC = (props: any) => {
  const tableRef = React.useRef<MPCListRef>(null);
  const pathArr = props?.location?.pathname?.split('/') || [];
  const [activeStatus, setActiveTab] = useState<string>(pathArr[2] || 'outIpTodo');
  // 详情弹框（点操作列打开，传 l2_id）
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [currentL2Id, setCurrentL2Id] = useState<number | undefined>(undefined);

  // 从路由读取 ip_id（hash 路由下 location.search 为 hash 内 query；兼容 URLSearchParams 解析）
  const getIpIdFromRoute = (): number | undefined => {
    const search = props?.location?.search || '';
    const ipId = Number(new URLSearchParams(search).get('ip_id'));
    return Number.isFinite(ipId) && ipId > 0 ? ipId : undefined;
  };

  // 打开详情：写 ip_id 到路由 hash 后 query（刷新/分享链接自动打开对应弹框）
  const openDetail = (l2Id: number | undefined) => {
    if (l2Id == null) return;
    setCurrentL2Id(l2Id);
    setDrawerVisible(true);
    props?.history?.push(`${pathArr.join('/')}?ip_id=${l2Id}`);
  };

  // 页面加载初始化 IP 菜单待办计数：MPCLayout/Header 只刷新默认 'out' 模块（GROUP_MODULE_MAP 无 outIp），
  // IP 待办 badge 仅靠 handleTabsChange 切 tab 时刷新；首次进入或点工具栏刷新按钮（MPCList 内部 tableRefresh
  // 只刷列表数据）时计数不更新 → 出现「待办数量为空(0)」而列表有数据。此处挂载即初始化一次。
  useEffect(() => {
    props?.getMenuCount?.('ip');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 关闭详情：移除路由 ip_id（避免刷新又自动打开）+ 刷新当前页列表（提交/暂存可能改变单据状态）
  const handleDrawerClose = () => {
    setDrawerVisible(false);
    if (getIpIdFromRoute()) {
      props?.history?.push(pathArr.join('/'));
    }
    tableRef.current?.refreshTable();
    // 关闭详情同步刷新 IP 待办计数（处理/提交/暂存后单据状态变化，badge 需重新拉取）
    props?.getMenuCount?.('ip');
  };

  // 路由参数自动打开：页面加载或路由变化时，若地址含 ip_id 则打开对应详情弹框
  useEffect(() => {
    const ipId = getIpIdFromRoute();
    if (ipId) {
      setCurrentL2Id(ipId);
      setDrawerVisible(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props?.location?.search]);

  // Tab 切换（待办/全部）
  const handleTabsChange = (key: string, level: number = 1) => {
    const offset = 1;
    pathArr[level + offset] = key;
    props?.getMenuCount?.('ip');
    props?.history?.push(pathArr.join('/') + props.location?.search);
    setActiveTab(key);
  };

  const items = getTabItemsFormProps(props);

  const columnsConfig = {
    demand_id: {
      width: 120,
      align: 'left' as const,
      // 点击需求单号打开详情抽屉（与操作列「处理/查看」一致，传 record.l2_id）
      render: (v: any, record: any) =>
        getCopyNode(
          v,
          <Button type="link" onClick={() => openDetail(record?.l2_id)}>
            {v}
          </Button>
        ),
    },
    demand_title: {
      width: 240,
      align: 'left' as const,
      render: (v: any) => <DotDotDot clamp={1}>{v || '-'}</DotDotDot>,
    },
    ip_name: { width: 160, align: 'left' as const },
    product_name: { width: 160, align: 'left' as const },
    estimated_amount: { width: 180, align: 'left' as const },
    status: {
      width: 120,
      align: 'left' as const,
      // 状态列用 Tag 样式（对齐走查：Offer谈判橙/Offer待确认蓝/Offer已确认绿）；未命中配置兜底原值
      render: (v: any) => {
        const conf = STATUS_TAG_CONF[v];
        if (!conf) return v || '-';
        return <Tag size="small" text={v} color={conf.color} />;
      },
    },
    business_users: {
      width: 160,
      align: 'left' as const,
      render: (v: any) => (Array.isArray(v) ? v.join('，') : v || '-'),
    },
    operation: {
      width: 80,
      fixed: 'right' as const,
      render: (_v: any, record: any) => {
        const isSubmit = record?.action === 'submit';
        return (
          <Button type="link" onClick={() => openDetail(record?.l2_id)}>
            {isSubmit ? '处理' : '查看'}
          </Button>
        );
      },
    },
  };

  // 筛选下拉 option：只拉非 noFetchOption 项（状态为固定枚举不拉），所属产品经 outsource filter_options 白名单 'product' 获取
  const fetchFilterOptions: MPCListProps['fetchFilterOptions'] = () => {
    return outsourceCommonFilterOptions({
      keys: FILTER_OPTIONS.filterKeys
        ?.filter((el: any) => !el.noFetchOption)
        ?.map((key: any) => getKey(key, 'optionKey')),
    });
  };

  const fetchList: MPCListProps['fetchList'] = (params) => {
    const { current, pageSize, conditions } = params;
    const api =
      routeToStateMap[activeStatus] === 'TODO'
        ? outsourceV2IpCooperationDemandPending
        : outsourceV2IpCooperationDemandAll;
    // 透传筛选条件：所属产品(product_ids) / 状态(states)，对应后端 IpCooperationCopyrightOwnerListForm 字段。
    // 产品筛选项 searchKey='product'（optionKey=ip_cooperation_copyright_owner_products），conditions.product 为产品ID数组；
    // ⚠️ 本地 genapi 生成的类型尚未含这两字段（后端未部署），用交叉类型声明；后端部署后重新 pnpm genapi 时字段并入原生类型，此声明可移除
    const requestParams: IpCooperationCopyrightOwnerListForm & {
      product_ids?: number[];
      states?: string[];
    } = {
      page_num: current,
      page_size: pageSize,
      keyword: conditions?.search_text,
      product_ids: conditions?.product,
      states: conditions?.states,
    };
    return api(requestParams).then((res: any) => ({
      data: {
        list: res?.data?.records || [],
        total_count: res?.data?.total || 0,
        columns: COLUMNS,
      },
    }));
  };

  return (
    <>
      <MPCList
        fetchList={fetchList}
        ref={tableRef}
        filterOptions={FILTER_OPTIONS}
        fetchFilterOptions={fetchFilterOptions}
        customColumnsConf={columnsConfig}
        // 整行点击打开详情（MPCList 内置 onClickRow + 行级 onRow；点击 A/checkbox/展开图标自动跳过）
        onClickRow={(record) => openDetail(record?.l2_id)}
        // @ts-ignore
        ProTablePlusProps={{
          toolbar: {
            title: <div className="pd-l-16">工作台</div>,
            tabs: {
              defaultActiveKey: activeStatus,
              onChange: (key: any) => handleTabsChange(key, 1),
              items,
            },
            actionsInRight: true,
          },
        }}
      />
      <DetailDrawer
        visible={drawerVisible}
        l2Id={currentL2Id}
        onClose={handleDrawerClose}
      />
    </>
  );
};

export default List;
